import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { ScreenType } from "@/types/user-profile";

interface UserPermissions {
  screens: ScreenType[];
  fullName: string | null;
  profileName: string | null;
  profileSlug: string | null;
  agenciaId: string | null;
  agenciaNome: string | null;
  agenciaLogoPath: string | null;
  agenciaCor: string | null;
  isLoading: boolean;
  /** Leitura das permissões falhou (depois das tentativas): nenhuma tela liberada. */
  isError: boolean;
  refetch: () => void;
}

type PermissionsData = Omit<UserPermissions, "isLoading" | "isError" | "refetch">;

// Falha fechada: erro ao ler permissões não libera nenhuma tela. Antes liberava
// um conjunto fixo que incluía Gestão de Perfis e Configurações, então um
// usuário de agência via telas internas numa falha pontual de leitura.

const EMPTY: PermissionsData = {
  screens: [],
  fullName: null,
  profileName: null,
  profileSlug: null,
  agenciaId: null,
  agenciaNome: null,
  agenciaLogoPath: null,
  agenciaCor: null,
};

export function useUserPermissions(): UserPermissions {
  const { user } = useAuth();

  // Chave estável por usuário: o React Query deduplica as N chamadas
  // concorrentes (ProtectedRoute + AppSidebar + 1 FreelancerActionsMenu por
  // linha de freelancer) numa ÚNICA request. Antes era useState/useEffect cru,
  // sem cache, disparando 1 GET v_profiles_with_access_profile por componente
  // -> N+1 em /freelancers (Sentry APP-1M).
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["user-permissions", user?.id],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<PermissionsData> => {
      const { data, error } = await supabase
        .from("v_profiles_with_access_profile")
        .select(
          "full_name, access_profile_screens, access_profile_name, access_profile_slug, agencia_id, agencia_nome, agencia_logo_path, agencia_cor"
        )
        .eq("id", user!.id)
        .maybeSingle();

      if (error) throw error;
      // Sem perfil de acesso: nenhuma tela (não é erro de leitura).
      if (!data) return EMPTY;

      return {
        screens: (data.access_profile_screens as ScreenType[]) || [],
        fullName: data.full_name || null,
        profileName: data.access_profile_name || null,
        profileSlug: data.access_profile_slug || null,
        agenciaId: data.agencia_id || null,
        agenciaNome: data.agencia_nome || null,
        agenciaLogoPath: data.agencia_logo_path || null,
        agenciaCor: data.agencia_cor || null,
      };
    },
  });

  return {
    ...(data ?? EMPTY),
    // Sem usuário não há o que carregar (estado resolvido), igual ao antigo
    // early-return com setIsLoading(false).
    isLoading: !!user && isLoading,
    isError: !!user && isError,
    refetch: () => void refetch(),
  };
}
